import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { hasPermission, ActionsEnum } from '@/lib/permissions';
import { dashboardRoutePaths } from '@/routes/route-config';
import type { Permission } from '@/types/auth.types';
import { useBranch } from '@/contexts/BranchContext';
import { usePlanFeatures } from '@/hooks/usePlanFeatures';
import { visibleSections } from '@/pages/settings/sections';
import {
  NAV_SECTIONS,
  type NavNode,
  type NavLeaf,
  type NavGroup,
  type NavSection,
  type ClinicRole,
} from './nav-config';

/**
 * Route matching, visibility filtering, and group expansion — the
 * three pieces of nav logic that were previously inlined (and
 * subtly wrong) inside the old 1667-line DashboardLayout.
 */

/**
 * Is `href` the active route for `pathname`?
 *
 * Prefix matching by default, which is the fix for the old shell's
 * `location.pathname === item.path`: a patient profile at
 * `/dashboard/patients/abc123` left "Patients" unhighlighted.
 *
 * The prefix is compared segment-wise so `/dashboard/reports` does
 * not match `/dashboard/reports-archive`.
 */
export function matchesPath(pathname: string, href: string, exact = false) {
  const path = href.split('?')[0];
  if (exact) return pathname === path;
  return pathname === path || pathname.startsWith(`${path}/`);
}

function leafActive(pathname: string, leaf: NavLeaf) {
  if (matchesPath(pathname, leaf.href, leaf.exact)) return true;
  return (leaf.alsoActiveFor ?? []).some((p) => matchesPath(pathname, p));
}

/**
 * A group is active when one of its links is, or when the URL falls under one
 * of its `activePrefixes`. The leaf check is what lets a group list its
 * prefixes page by page instead of claiming a whole URL subtree — Finance
 * needs that, because two pages under `/dashboard/finance/…` belong to
 * Reports — while its exact-matched Overview root still lights it up.
 */
function groupActive(pathname: string, group: NavGroup) {
  if (
    group.children.some((c) => c.kind === 'link' && leafActive(pathname, c))
  ) {
    return true;
  }
  return group.activePrefixes.some((p) =>
    p.endsWith('/') ? pathname.startsWith(p) : matchesPath(pathname, p),
  );
}

/** Visibility: permission gate, role gate and branch-count gate must all pass. */
function isVisible(
  node: {
    resource?: string | null;
    roles?: ClinicRole[];
    multiBranchOnly?: boolean;
  },
  permissions: Permission[] | undefined,
  role: ClinicRole | undefined,
  multiBranch: boolean,
) {
  if (node.multiBranchOnly && !multiBranch) return false;
  if (
    node.resource &&
    !hasPermission(permissions, node.resource, ActionsEnum.VIEW)
  ) {
    return false;
  }
  // An unknown/absent role is treated as ADMIN-equivalent, matching the
  // old shell's `|| !role` fallbacks — but expressed once, not per item.
  if (node.roles && role && !node.roles.includes(role)) return false;
  return true;
}

export interface VisibleSection extends NavSection {
  items: NavNode[];
}

export function useVisibleNav(
  permissions: Permission[] | undefined,
  role?: ClinicRole,
) {
  // Branches the user can work in (archived ones are already left out).
  const { branches } = useBranch();
  const multiBranch = branches.length > 1;
  const { has } = usePlanFeatures();
  return useMemo<VisibleSection[]>(() => {
    return NAV_SECTIONS.map((section) => ({
      ...section,
      items: section.items
        .filter((item) => isVisible(item, permissions, role, multiBranch))
        .map((item) => {
          if (item.kind !== 'group') return item;
          // Filter the group's children too, then drop a group that
          // ends up empty — hiding, never disabling.
          const groupLocked = !has(item.feature);
          const children = item.children
            .filter((c) =>
              c.kind === 'subheading'
                ? true
                : isVisible(c, permissions, role, multiBranch),
            )
            .map((c) =>
              c.kind === 'link'
                ? { ...c, locked: groupLocked || !has(c.feature) }
                : c,
            );
          // Strip subheadings whose entire block was filtered away.
          const pruned = children.filter((c, i) => {
            if (c.kind !== 'subheading') return true;
            const next = children[i + 1];
            return next !== undefined && next.kind !== 'subheading';
          });
          const links = pruned.filter((c) => c.kind === 'link');
          return {
            ...item,
            children: pruned,
            locked: links.length > 0 && links.every((c) => c.locked),
          };
        })
        .map((item) =>
          item.kind === 'link' ? { ...item, locked: !has(item.feature) } : item,
        )
        .filter((item) => item.kind !== 'group' || item.children.length > 0),
    })).filter((section) => section.items.length > 0);
  }, [permissions, role, multiBranch, has]);
}

/** The settings sections this user may open — the same list the router guards. */
export function useVisibleSettings(permissions: Permission[] | undefined) {
  return useMemo(() => visibleSections(permissions), [permissions]);
}

/**
 * Active-state resolution for the current URL.
 *
 * Settings sections are real paths now, so this is a pathname match on the
 * section segment. Matching the segment — rather than `startsWith` on the
 * whole href — is what keeps `/settings/staff/:staffId` lighting up "Staff"
 * while never letting one section name prefix-collide with another.
 */
export function useActiveNav() {
  const { pathname } = useLocation();

  const isLeafActive = useCallback(
    (leaf: NavLeaf) => leafActive(pathname, leaf),
    [pathname],
  );
  const isGroupActive = useCallback(
    (group: NavGroup) => groupActive(pathname, group),
    [pathname],
  );
  const isSettingsActive = useCallback(
    (sectionId: string) => {
      const segment = pathname.split('/')[3];
      return (
        pathname.startsWith(`${dashboardRoutePaths.settings}/`) &&
        segment === sectionId
      );
    },
    [pathname],
  );
  const inSettings = matchesPath(pathname, dashboardRoutePaths.settings);
  const onSettingsHome = matchesPath(
    pathname,
    dashboardRoutePaths.settings,
    true,
  );

  return {
    pathname,
    isLeafActive,
    isGroupActive,
    isSettingsActive,
    inSettings,
    onSettingsHome,
  };
}
